import { useEffect, useState } from 'react';

/**
 * 地区別ニックネーム (REQ-4.1.3 — 地区ごとに独立した nickname)。
 *
 * Phase 2 minimum: localStorage で districtId 別保管。
 * Phase 3+: Firestore `participants/{uid}/memberships/{districtId}.nickname` に移行
 *          (旧 SettingsModal.tsx L130 `setMeta('nickname:${districtId}', ...)` 相当)。
 *
 * 旧 SettingsModal.tsx + UserFieldsForm.tsx (L3 参照、docs/97 §3) のうち、
 * Phase 2 では nickname のみ扱う。gender / ageGroup / relationship / attribute /
 * profileFieldValues は Phase 5 で features/auth/profile 拡張時に対応。
 */

const STORAGE_KEY_PREFIX = 'cw.nickname.v1.';

function storageKey(districtId: string): string {
  return `${STORAGE_KEY_PREFIX}${districtId}`;
}

function loadNickname(districtId: string | null): string {
  // 地区フォールバック根絶: districtId 未解決 (null/空) のときは phase2-default の
  // 空 localStorage を読まず、単に空文字を返す (購読・読込なし)。
  if (!districtId) return '';
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(storageKey(districtId)) ?? '';
  } catch {
    return '';
  }
}

function persistNickname(districtId: string | null, nickname: string): void {
  if (!districtId) return;
  if (typeof localStorage === 'undefined') return;
  try {
    if (nickname === '') {
      localStorage.removeItem(storageKey(districtId));
    } else {
      localStorage.setItem(storageKey(districtId), nickname);
    }
  } catch {
    /* quota / private mode 等は無視 */
  }
}

export function useNickname(districtId: string | null): {
  nickname: string;
  setNickname: (value: string) => void;
} {
  const [nickname, setNicknameState] = useState<string>(() => loadNickname(districtId));

  // districtId 変更時に localStorage から再ロード (props-derived state パターン)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNicknameState(loadNickname(districtId));
  }, [districtId]);

  function setNickname(value: string): void {
    const trimmed = value.trim();
    setNicknameState(trimmed);
    persistNickname(districtId, trimmed);
  }

  return { nickname, setNickname };
}
